import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosBackend from "../api/axiosBackend";
import OrdersQueryDialog from "../components/OrdersComponents/OrdersQueryDialog";
import OrderItem from "../components/OrdersComponents/OrderItem";
import { InsertOrderDialog } from "pages/InsertOrder";

// shadcn
import { Card, CardContent } from "components/ui/card";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortParams, setSortParams] = useState({ sortField: "", sortOrder: "" });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const queryParams = useMemo(
    () => ({ ...filters, ...sortParams }),
    [filters, sortParams]
  );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const response = await axiosBackend.get("/orders/get", {
        params: queryParams,
        withCredentials: true,
      });
      setOrders(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setErrorMsg("אירעה שגיאה בעת טעינת ההזמנות");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const response = await axiosBackend.get("/orders/get", {
          params: queryParams,
          withCredentials: true,
        });

        if (!alive) return;
        setOrders(response.data?.data || []);
      } catch (error) {
        if (!alive) return;
        console.error("Error fetching orders:", error);
        setErrorMsg("אירעה שגיאה בעת טעינת ההזמנות");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [queryParams]);

  const handleApplyQuery = useCallback((newFilters, newSort) => {
    setFilters(newFilters || {});
    setSortParams(newSort || { sortField: "", sortOrder: "" });
  }, []);

  const handleInlineSaved = useCallback((orderId, patch) => {
    setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, ...patch } : o)));
  }, []);

  const handleStatusChange = useCallback(async (orderId, newStatus) => {
    // optimistic UI
    setOrders((prev) =>
      prev.map((o) => (o._id === orderId ? { ...o, status: newStatus } : o))
    );

    try {
      await axiosBackend.put(`/orders/change-status/${orderId}`, { status: newStatus });
    } catch (error) {
      console.error("Error updating order status:", error);
      // rollback by refetching (simplest and reliable)
      fetchOrders();
    }
  }, [fetchOrders]);

  const handleCreated = useCallback((order) => {
    // prepend for better UX (newest first) - change to append if you prefer
    setOrders((prev) => [order, ...prev]);
  }, []);

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">הזמנות</h1>

          <div className="flex items-center gap-2">
            <OrdersQueryDialog
              onApply={handleApplyQuery}
              initialFilters={filters}
              initialSort={sortParams}
            />
            <InsertOrderDialog onCreated={handleCreated} />
          </div>
        </div>

        {/* States */}
        {errorMsg && (
          <Card className="mb-4">
            <CardContent className="pt-6 text-sm text-destructive">{errorMsg}</CardContent>
          </Card>
        )}

        {loading && (
          <Card className="mb-4">
            <CardContent className="pt-6 text-sm text-muted-foreground">טוען הזמנות…</CardContent>
          </Card>
        )}

        {!loading && !errorMsg && orders.length === 0 && (
          <Card className="mb-4">
            <CardContent className="pt-6 text-sm text-muted-foreground">
              לא נמצאו הזמנות לפי הסינון הנוכחי.
            </CardContent>
          </Card>
        )}

        {/* Orders */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {orders.map((order) => (
            <OrderItem
              key={order._id}
              order={order}
              onSaved={handleInlineSaved}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Order;
